import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Feather, Loader2, Upload, X, BookmarkPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/thesis`;

/**
 * Writing voice for this project: a preset, or a profile extracted from a paper
 * the author admires. Only the STYLE is imitated — never wording or content.
 */
export default function WritingStylePanel({ userId, project, onUpdated }) {
  const [presets, setPresets] = useState([]);
  const [sample, setSample] = useState('');
  const [busy, setBusy] = useState('');
  const [style, setStyle] = useState(project?.preferred_style || '');
  const [source, setSource] = useState(project?.style_source || '');
  const [library, setLibrary] = useState([]);

  const loadLibrary = () => axios.get(`${API}/style-library`, { params: { user_id: userId } })
    .then(({ data }) => setLibrary(data.voices || []))
    .catch(() => setLibrary([]));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLibrary(); }, [userId]);

  const saveToLibrary = async () => {
    const name = window.prompt('Name this voice', source?.startsWith('preset')
      ? source.split(':')[1] : 'My voice');
    if (!name) return;
    try {
      await axios.post(`${API}/style-library`, { name, style, source },
        { params: { user_id: userId } });
      toast.success('Saved to your style library');
      loadLibrary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the voice');
    }
  };

  useEffect(() => {
    axios.get(`${API}/style-presets`, { params: { user_id: userId } })
      .then(({ data }) => setPresets(data.presets || []))
      .catch(() => setPresets([]));
  }, [userId]);

  const save = async (body, label) => {
    setBusy(label);
    try {
      const { data } = await axios.post(`${API}/projects/${project.id}/style-profile`, body,
        { params: { user_id: userId } });
      setStyle(data.preferred_style);
      setSource(data.style_source);
      toast.success(data.preferred_style ? 'Writing voice updated' : 'Writing voice cleared');
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the writing voice');
    } finally {
      setBusy('');
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API}/projects/${project.id}/style-profile/upload`, form,
        { params: { user_id: userId }, headers: { 'Content-Type': 'multipart/form-data' } });
      setStyle(data.preferred_style);
      setSource(data.style_source);
      toast.success('Style learned from your sample');
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read that file');
    } finally {
      setBusy('');
    }
  };

  return (
    <Card data-testid="writing-style-panel">
      <CardHeader className="pb-3">
        <CardTitle className="font-serif flex items-center gap-2"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Feather className="w-4 h-4 text-[#1d4ed8]" /> Writing style
        </CardTitle>
        <CardDescription>
          Choose a voice, or let InkPhD learn the voice of a paper you admire. Only the style is
          imitated — sentence rhythm, hedging, citation placement, paragraph shape.
          <strong> Never the wording or the content.</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {style ? (
          <div className="rounded-lg border border-[#1d4ed8]/35 bg-[#1d4ed8]/[0.05] p-3"
            data-testid="writing-style-current">
            <div className="flex items-start justify-between gap-3 mb-1">
              <Badge variant="outline" className="text-[10px] uppercase tracking-widest">
                {source?.startsWith('preset') ? `Preset · ${source.split(':')[1]}`
                  : source?.startsWith('sample') ? 'Learned from your sample' : 'Custom'}
              </Badge>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                  onClick={saveToLibrary} data-testid="writing-style-save-library">
                  <BookmarkPlus className="w-3 h-3 mr-1" />Save to library
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                  disabled={busy === 'clear'} onClick={() => save({ clear: true }, 'clear')}
                  data-testid="writing-style-clear">
                  <X className="w-3 h-3 mr-1" />Clear
                </Button>
              </div>
            </div>
            <p className="text-[13px] leading-relaxed">{style}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="writing-style-empty">
            No voice set — generations use the default academic style for this document type.
          </p>
        )}

        {library.length > 0 && (
          <div data-testid="style-library">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              My style library
            </p>
            <div className="flex flex-wrap gap-2">
              {library.map((v) => (
                <span key={v.id} className="inline-flex items-center gap-1 rounded-full border
                  border-[#1d4ed8]/40 bg-[#1d4ed8]/[0.05] pl-3 pr-1 py-1 text-[12px]">
                  <button type="button" disabled={!!busy}
                    onClick={() => save({ style_text: v.style }, 'library')}
                    title="Apply this voice to the open project"
                    data-testid={`style-library-use-${v.id}`} className="font-semibold">
                    {v.name}
                  </button>
                  <button type="button" title="Delete" data-testid={`style-library-delete-${v.id}`}
                    onClick={async () => {
                      await axios.delete(`${API}/style-library/${v.id}`, { params: { user_id: userId } });
                      loadLibrary();
                    }}
                    className="p-1 text-muted-foreground hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Ready-made voices
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {presets.map((p) => (
              <button key={p.key} type="button" disabled={!!busy}
                onClick={() => save({ preset: p.key }, p.key)}
                data-testid={`writing-style-preset-${p.key}`}
                className="text-left rounded-lg border border-border p-3 hover:border-[#1d4ed8]/50
                  transition-colors disabled:opacity-60">
                <p className="text-[13px] font-semibold flex items-center gap-2">
                  {p.label}
                  {busy === p.key && <Loader2 className="w-3 h-3 animate-spin" />}
                </p>
                <p className="text-[11.5px] text-muted-foreground leading-snug mt-1 line-clamp-3">
                  {p.style}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Learn from a paper you admire
          </p>
          <Textarea rows={4} value={sample} onChange={(e) => setSample(e.target.value)}
            placeholder="Paste 2–3 paragraphs written by the author whose voice you want (at least 400 characters)…"
            data-testid="writing-style-sample-input" />
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" disabled={busy === 'sample' || sample.trim().length < 400}
              onClick={() => save({ sample_text: sample }, 'sample')}
              className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full"
              data-testid="writing-style-learn-btn">
              {busy === 'sample' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Learn this voice
            </Button>
            <label className="inline-flex">
              <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                data-testid="writing-style-file-input"
                onChange={(e) => upload(e.target.files?.[0])} />
              <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full
                border border-border px-3.5 py-1.5 cursor-pointer hover:border-[#1d4ed8]/50 transition-colors">
                {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload a paper (PDF/DOCX)
              </span>
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            The sample is used once to describe the style and is not stored as evidence, never
            retrieved and never quoted.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
